import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'

import { MultiSelect } from '~/components/common/MultiSelect'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import type {
  FilterGridItem,
  ToolbarStatusItem,
} from '~/components/common/filters'
import {
  NO_WORKFLOW_FILTER_VALUE,
  type SharedBidListFilter,
} from '~/pages/SpotBidding/models/sharedBidListFilter'
import {
  SPOT_BID_STATUS_FILTER_CATEGORIES,
  type SpotBidStatusCategory,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'

type SideRadioValue = '' | 'buy' | 'sell'

const SIDES_OF_RADIO: Record<SideRadioValue, ReadonlySet<Side>> = {
  '': new Set(),
  buy: new Set([Side.BUY]),
  sell: new Set([Side.SELL]),
}

// 値は Set (空 = すべて) で持ち、 部品は design の「すべて / 買 / 売」 の radio (#12355 で Des が部品の型を
// 決めるまで今の radio のまま)。 両方選んだ Set は radio では「すべて」 と同じ絞り込みなので「すべて」 に出す。
const radioValueOf = (sides: ReadonlySet<Side>): SideRadioValue => {
  if (sides.size !== 1) return ''
  return sides.has(Side.BUY) ? 'buy' : 'sell'
}

const isStatusFilterCategory = (
  value: string,
): value is SpotBidStatusCategory =>
  (SPOT_BID_STATUS_FILTER_CATEGORIES as readonly string[]).includes(value)

type SharedBidListFilterItems = {
  /** 開いたカードの項目 (売/買 → ステータス)。 */
  gridItems: FilterGridItem[]
  /** 閉じた時の要約。 既定でも「すべて」と出す (空白にしない)。 */
  summary: ToolbarStatusItem[]
  /** 既定から変えている条件の数 (開閉式の件数に足す)。 */
  modifiedCount: number
}

/**
 * 通常入札タブ・ブロック入札タブの一覧の開閉式フィルターに置く、 両タブで共有する絞り込み
 * (AC-BSP-019 の売/買・ステータス・ワークフロー)。 両タブが同じ項目を描き、 値は page が持つ 1 つの状態を受け取る。
 * 左のサイドバーは開閉式 (表の直上) に替えたので、 部品でなく開閉式の項目・要約・件数を返す。
 * クリアは各タブの resetFilters が page の値を既定に戻す (両タブが同時に戻る)。
 */
export const useSharedBidListFilterItems = (
  value: SharedBidListFilter,
  onChange: (next: SharedBidListFilter) => void,
  /** ワークフローの選択肢にするワークフロー ID (「ワークフローなし」 はここで足す)。 */
  workflowIds: readonly string[],
): SharedBidListFilterItems => {
  const { t } = useTranslation()
  const sideSummaryLabel: Record<SideRadioValue, string> = {
    '': t('common:filter.all'),
    buy: t('page:biddingSpot.side.buy'),
    sell: t('page:biddingSpot.side.sell'),
  }
  const sideValue = radioValueOf(value.sides)
  return {
    gridItems: [
      {
        key: 'side',
        label: t('page:biddingSpot.sidebar.filterSide'),
        control: (
          <TradeSideRadioGroup
            allLabel={t('page:biddingSpot.sidebar.filterSideAll')}
            ariaLabel={t('page:biddingSpot.sidebar.filterSide')}
            name="spot-bid-side-filter"
            onChange={(next) =>
              onChange({ ...value, sides: SIDES_OF_RADIO[next] })
            }
            value={sideValue}
            values={{ all: '', buy: 'buy', sell: 'sell' }}
          />
        ),
      },
      {
        key: 'status',
        label: t('page:biddingSpot.sidebar.filterStatus'),
        // 選択肢は AC-BSP-019a の 11 区分。 [2026-09-29 デザイナー指摘] checkbox つきの multi-select は
        // DS に無いので、 DS 準拠の `MultiSelect` (chip + typeahead) を使う。
        control: (
          <Box width="280px">
            <MultiSelect
              aria-label={t('page:biddingSpot.sidebar.filterStatus')}
              onChange={(next) =>
                onChange({
                  ...value,
                  statuses: new Set(next.filter(isStatusFilterCategory)),
                })
              }
              options={SPOT_BID_STATUS_FILTER_CATEGORIES.map((category) => ({
                value: category,
                label: t(`page:biddingSpot.bidStatusCategory.${category}`),
              }))}
              placeholder={t('page:biddingSpot.sidebar.filterStatusAll')}
              selected={[...value.statuses]}
            />
          </Box>
        ),
      },
      {
        key: 'workflow',
        label: t('page:biddingSpot.sidebar.filterWorkflow'),
        control: (
          <Box width="280px">
            <MultiSelect
              aria-label={t('page:biddingSpot.sidebar.filterWorkflow')}
              onChange={(next) =>
                onChange({ ...value, workflows: new Set(next) })
              }
              options={[
                ...workflowIds.map((id) => ({ value: id, label: id })),
                {
                  value: NO_WORKFLOW_FILTER_VALUE,
                  label: t('page:biddingSpot.sidebar.filterWorkflowNone'),
                },
              ]}
              placeholder={t('page:biddingSpot.sidebar.filterWorkflowAll')}
              selected={[...value.workflows]}
            />
          </Box>
        ),
      },
    ],
    summary: [
      {
        key: 'side',
        label: t('page:biddingSpot.sidebar.filterSide'),
        value: sideSummaryLabel[sideValue],
      },
      {
        key: 'status',
        label: t('page:biddingSpot.sidebar.filterStatus'),
        value:
          value.statuses.size === 0
            ? t('common:filter.all')
            : t('common:filter.selectedCount', { count: value.statuses.size }),
      },
      {
        key: 'workflow',
        label: t('page:biddingSpot.sidebar.filterWorkflow'),
        value:
          value.workflows.size === 0
            ? t('common:filter.all')
            : t('common:filter.selectedCount', {
                count: value.workflows.size,
              }),
      },
    ],
    modifiedCount:
      (value.sides.size > 0 ? 1 : 0) +
      (value.statuses.size > 0 ? 1 : 0) +
      (value.workflows.size > 0 ? 1 : 0),
  }
}
